<template>
  <div class="ca-period">
    <div class="ca-period__field">
      <DatePicker
        class="ca-period__date"
        :value="date"
        :clearable="false"
        :disabled="disabled"
        :disabled-dates="isFutureDate"
        @input="$emit('date-change', $event)"
      />
    </div>

    <div class="ca-period__field ca-period__field--shift">
      <Select
        class="ca-period__select form-select"
        mdClass="ca-period-menu"
        :value="shiftId"
        :multiple="false"
        :disabled="disabled || !shifts.length"
        :placeholder="$t('shift')"
        @input="$emit('shift-change', $event)"
      >
        <Option
          v-for="shift in shifts"
          :key="shift.id"
          :value="shift.id"
          :label="shift.shift_name || `#${shift.id}`"
        />
      </Select>
    </div>

    <button
      type="button"
      class="ca-period__apply"
      :class="{ 'ca-period__apply--ready': canApply }"
      :disabled="!canApply"
      @click="$emit('apply')"
    >
      {{ $t('ca_apply') }}
    </button>

    <button
      v-if="!isLive"
      type="button"
      class="ca-period__live"
      :disabled="disabled || !canReturnToLive"
      @click="$emit('return-live')"
    >
      <span class="ca-period__live-dot" aria-hidden="true"></span>
      {{ $t('live') }}
    </button>
  </div>
</template>

<script>
import DatePicker from '../../../common/components/DatePicker'
import Select from '../../../common/components/Select'
import Option from '../../../common/components/Option'
import { toLocalDateStr } from './cleanerActivityUtils'

export default {
  components: { DatePicker, Select, Option },
  props: {
    date: { type: String, default: '' },
    shiftId: { type: [Number, String], default: null },
    shifts: { type: Array, default: () => [] },
    maxDate: { type: String, default: '' },
    isLive: { type: Boolean, default: false },
    canReturnToLive: { type: Boolean, default: false },
    canApply: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
  },
  methods: {
    isFutureDate(date) {
      if (!this.maxDate) return false
      const dateStr = toLocalDateStr(date)
      return !!dateStr && dateStr > this.maxDate
    },
  },
}
</script>

<style lang="scss" scoped>
@use "./caControls" as *;

.ca-period {
  display: flex;
  align-items: flex-end;
  gap: 10px;
  flex-wrap: wrap;

  &__field {
    min-width: 160px;

    &--shift {
      width: 220px;
    }
  }

  // Highlighted only while the staged period differs from what is on screen, so the button
  // doubles as the "you have unapplied changes" cue.
  &__apply {
    @include ca-apply-button('&--ready');
  }

  &__live {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-height: 34px;
    padding: 0 12px;
    border: 1px solid #cfe2c8;
    border-radius: 6px;
    background: #fff;
    color: #4f873a;
    font-size: 0.8rem;
    font-weight: 700;
    cursor: pointer;

    &:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }
  }

  &__live-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #5f9b46;
  }

  :deep(.ca-period__date.md-datepicker) {
    @include ca-compact-datepicker;
    width: 100%;
  }

  :deep(.ca-period__select.select-container.md-field) {
    @include ca-compact-select;
  }
}

@media (max-width: 767px) {
  .ca-period {
    align-items: stretch;
    width: 100%;

    &__field,
    &__field--shift,
    &__apply,
    &__live {
      width: 100%;
    }

    &__live {
      justify-content: center;
    }
  }
}
</style>

<style lang="scss">
.ca-period-menu.md-menu-content {
  max-height: 280px !important;
}
</style>
